/**
 * App-side reactions for the note-generation engine. Imported only from
 * `scribeRecordModel` so these value imports evaluate with the model, after
 * the store exists. The engine itself loads earlier, through `@heidi/session/data`.
 */
import { USE_RECORDING_V2 } from '@/app-shell/flags/capture-flags';
import { getChronicleDeviceFileNameForSession } from '@/atoms/chronicle/session-device-files';
import {
  chooseNoteStructureModalOutOfProActionsAtom,
  chooseNoteStructureModalTemplateUnavailableAtom,
  chooseNoteStructureModalUnavailableTemplateIdAtom,
  chooseNoteStructureModalUnavailableTemplateWasDefaultAtom,
  isChooseNoteStructureModalDismissibleAtom,
  isChooseNoteStructureModalOpenAtom,
} from '@/atoms/noteStructureAtoms';
import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { hyperspaceSectionalRestoreAttemptsFamily } from '@/components/ui/choose-note-structure-modal/choose-note-structure-command/atoms';
import { countMarkdownSections } from '@/helpers/sectionParser';
import { getNavigatorOnlineSnapshot } from '@/hooks/desktop/useNavigatorOnline';
import { GET_AI_COMMANDS_BY_SESSION_ID } from '@/hooks/query/useQueryAICommandsBySessionId';
import { GET_BACKUP_AUDIO } from '@/hooks/query/useQueryBackupAudio';
import { GET_DOCUMENT_AI_COMMANDS_BY_SESSION_ID } from '@/hooks/query/useQueryDocumentAICommandsBySessionId';
import {
  noteGenerationFinished,
  noteGenerationStart,
} from '@/lib/analytics/scribe/general';
import {
  classifyNoteGenerateFailureCode,
  createNoteGenerateCancelledError,
  createRecoveredStreamFailureError,
  interruptionMetadata,
  isFetchFailure,
  isNoteGenerateError,
} from '@/lib/telemetry/noteGenerateTracking';
import { isConfirmedDesktopOfflineNetworkMode } from '@/utils/http/desktopOfflineNetwork';
import { getIsSectionalTemplateFromCache } from '@/utils/templateCacheHelpers';
import {
  delayUpgradeExtendAction,
  QuotaExceedType,
  shouldShowDelayUpgradeModal,
} from '@heidi/commerce';
import { toast } from '@heidi/design-system/toast';
import { formatMessageOutsideReact } from '@heidi/runtime/i18n';
import { NoteGenerationErrorCode } from '@heidi/session/contracts';
import { configureNoteGenerationEngine } from '@heidi/session/data';
import { getDefaultStore } from 'jotai';
import { RESET } from 'jotai/utils';

import { streamResponseMessages } from './session-data-wiring-note-generation.messages';

const jotaiStore = getDefaultStore();

function openNoteStructurePicker(): void {
  jotaiStore.set(isChooseNoteStructureModalOpenAtom, () => true);
}

function reopenNoteStructurePickerForRecovery(): void {
  jotaiStore.set(isChooseNoteStructureModalDismissibleAtom, () => true);
  jotaiStore.set(isChooseNoteStructureModalOpenAtom, () => true);
}

function openDelayUpgradeModalForStreamQuota(): void {
  jotaiStore.set(shouldShowDelayUpgradeModal, () => true);
  jotaiStore.set(delayUpgradeExtendAction, () => QuotaExceedType.STREAM_NOTE);
}

function markPickerOutOfProActions(): void {
  jotaiStore.set(chooseNoteStructureModalOutOfProActionsAtom, () => true);
}

/** Preserve the body's eager default decision between the first and second writes. */
function showPickerUnavailableTemplateBanner(producers: {
  templateId: () => string | null;
  computeWasDefault: () => boolean;
}): void {
  jotaiStore.set(chooseNoteStructureModalUnavailableTemplateIdAtom, producers.templateId);
  const wasDefault = producers.computeWasDefault();
  jotaiStore.set(
    chooseNoteStructureModalUnavailableTemplateWasDefaultAtom,
    () => wasDefault
  );
  jotaiStore.set(chooseNoteStructureModalTemplateUnavailableAtom, () => true);
}

/** Call only behind the body's existing surface/arm guards. */
function resetSectionalRestoreAttempts(sessionId: string): void {
  jotaiStore.set(hyperspaceSectionalRestoreAttemptsFamily(sessionId), RESET);
}

/** The caller owns the cap check and any failure toast. */
function recordSectionalRestoreAttempt(sessionId: string, next: number): void {
  jotaiStore.set(hyperspaceSectionalRestoreAttemptsFamily(sessionId), next);
}

function toastGenerationStoppedAndRestored(): void {
  toast({
    title: 'Generating stopped and previous version restored',
  });
}

function toastGenerationStopped(): void {
  toast({
    title: 'Generating stopped',
  });
}

function toastSectionalConversionFailed(): void {
  toast({
    tone: 'destructive',
    title: formatMessageOutsideReact(
      streamResponseMessages.sectionalConversionFailedTitle
    ),
    description: formatMessageOutsideReact(
      streamResponseMessages.sectionalConversionFailedDescription
    ),
  });
}

/**
 * Pre-redesign toast for an unavailable template, one per code. Used with the
 * pro-actions flag OFF, and for Haiku either way (it mounts no picker).
 * Pre-formatted strings, not `FormattedMessage` elements, so toast error
 * tracking can extract an identifiable message.
 */
function toastUnavailableTemplate(code: string): void {
  switch (code) {
    case NoteGenerationErrorCode.TeamTemplateAccessDenied:
      toast({
        // Stable E2E selector.
        'data-testid': 'note-generation-team-template-access-toast',
        tone: 'destructive',
        title: 'Error',
        description:
          'You do not have access to this team template, please contact your administrator',
      });
      return;
    case NoteGenerationErrorCode.TemplateDeleted:
    case NoteGenerationErrorCode.TemplateNotFound:
      toast({
        // Stable E2E selector.
        'data-testid': 'note-generation-template-deleted-toast',
        tone: 'destructive',
        title: formatMessageOutsideReact(streamResponseMessages.templateUnavailableTitle),
        description: formatMessageOutsideReact(
          streamResponseMessages.templateDeletedDescription
        ),
      });
      return;
    default:
      toast({
        // Stable E2E selector.
        'data-testid': 'note-generation-template-unavailable-toast',
        tone: 'destructive',
        title: formatMessageOutsideReact(streamResponseMessages.templateUnavailableTitle),
        description: formatMessageOutsideReact(
          streamResponseMessages.templateUnavailableDescription
        ),
      });
  }
}

configureNoteGenerationEngine({
  reactions: {
    openNoteStructurePicker,
    reopenNoteStructurePickerForRecovery,
    openDelayUpgradeModalForStreamQuota,
    markPickerOutOfProActions,
    showPickerUnavailableTemplateBanner,
    resetSectionalRestoreAttempts,
    recordSectionalRestoreAttempt,
    toastGenerationStoppedAndRestored,
    toastGenerationStopped,
    toastSectionalConversionFailed,
    toastUnavailableTemplate,
  },
  analytics: {
    noteGenerationStart: (input) => {
      noteGenerationStart(input);
    },
    noteGenerationFinished: (input) => {
      noteGenerationFinished(input);
    },
  },
  telemetry: {
    classifyNoteGenerateFailureCode: (code) => classifyNoteGenerateFailureCode(code),
    createNoteGenerateCancelledError: () => createNoteGenerateCancelledError(),
    createRecoveredStreamFailureError: (outcome, failureReason) =>
      createRecoveredStreamFailureError(outcome, failureReason),
    interruptionMetadata: (error) => interruptionMetadata(error),
    isFetchFailure: (error) => isFetchFailure(error),
    isNoteGenerateError: (error): error is Error => isNoteGenerateError(error),
  },
  isRecordingV2Enabled: () => USE_RECORDING_V2(),
  isNavigatorOnline: () => getNavigatorOnlineSnapshot(),
  isConfirmedDesktopOfflineNetworkMode: () => isConfirmedDesktopOfflineNetworkMode(),
  readOfflineConnectivityState: () => getDefaultStore().get(offlineConnectivityStateAtom),
  getChronicleDeviceFileNameForSession: (sessionId) =>
    getChronicleDeviceFileNameForSession(sessionId),
  countMarkdownSections: (markdown) => countMarkdownSections(markdown),
  isSectionalTemplate: (id) => getIsSectionalTemplateFromCache(id),
  // Getters, not eager reads. These hook modules import the store, which
  // imports this file, so the constants are still in TDZ while this module
  // evaluates. Invalidators read the heads at call time.
  queryKeyHeads: {
    get aiCommandsBySessionId() {
      return GET_AI_COMMANDS_BY_SESSION_ID;
    },
    get documentAiCommandsBySessionId() {
      return GET_DOCUMENT_AI_COMMANDS_BY_SESSION_ID;
    },
    get backupAudio() {
      return GET_BACKUP_AUDIO;
    },
  },
});
